import * as UI from "@iyk/ui"
import * as React from "react"
import { useMediaQuery } from "../hooks/useMediaQuery.tsx"
import { Drawer } from "./Drawer.tsx"

const Root = ({ breakpoint = "640px", ...props }: DrawerDropdown.Root.Props) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const isDesktop = useMediaQuery(`(min-width: ${breakpoint})`)

  return (
    <Context.Provider value={{ isDesktop, isOpen, setIsOpen }}>
      {isDesktop ? (
        <UI.Dropdown.Root open={isOpen} onOpenChange={setIsOpen} {...props} />
      ) : (
        <Drawer.Root open={isOpen} onOpenChange={setIsOpen} {...props} />
      )}
    </Context.Provider>
  )
}

const Trigger = (props: DrawerDropdown.Trigger.Props) => {
  const { isDesktop } = useContext()

  if (isDesktop) {
    return <UI.Dropdown.Trigger {...props} />
  }

  return <Drawer.Trigger {...props} />
}

const Content = ({ dropdownProps, ...props }: DrawerDropdown.Content.Props) => {
  const { isDesktop } = useContext()

  if (isDesktop) {
    return <UI.Dropdown.Content className="gap-1.5" {...dropdownProps} {...props} />
  }

  return (
    <Drawer.Content>
      <div className="flex flex-col gap-3">{props.children}</div>
    </Drawer.Content>
  )
}

const MenuRoot = (props: DrawerDropdown.Menu.Root.Props) => {
  return <div className="flex flex-col gap-1 sm:gap-0" {...props} />
}

const MenuItem = (props: DrawerDropdown.Menu.Item.Props) => {
  return React.cloneElement(props.children, {
    className: UI.cx(
      "cursor-pointer",
      "text-sm rounded-md px-2",
      "h-9",
      "flex items-center gap-2 grow",
      "hover:bg-gray-3 focus:bg-gray-3",
      "dark:hover:bg-gray-5 dark:focus:bg-gray-5",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      "relative select-none outline-none"
    )
  })
}

const Item = ({ className, ...props }: DrawerDropdown.Item.Props) => {
  return <div className={UI.cx("h-9 flex items-center gap-3 sm:px-3", className)} {...props} />
}

const Separator = () => {
  return <div className="h-px bg-gray-5 sm:-mx-3" />
}

export namespace DrawerDropdown {
  export namespace Root {
    export type Props = {
      breakpoint?: `${number}px`
      children: React.ReactNode
    }
  }

  export namespace Trigger {
    export type Props = {
      "data-testid"?: string
      className?: string
      children: React.ReactNode
    }
  }

  export namespace Content {
    export type Props = {
      children: React.ReactNode
      dropdownProps?: UI.Dropdown.Content.Props
    }
  }

  export namespace Item {
    export type Props = {
      className?: string
      children: React.ReactNode
    }
  }

  export namespace Menu {
    export namespace Root {
      export type Props = {
        children: React.ReactNode
      }
    }

    export namespace Item {
      export type Props = {
        children: React.ReactElement<any>
      }
    }
  }
}

export const DrawerDropdown = {
  Root,
  Trigger,
  Content,
  Item,
  Separator,
  Menu: {
    Root: MenuRoot,
    Item: MenuItem
  }
}

const noopContext: Context = {
  isDesktop: false,
  isOpen: false,
  setIsOpen: () => {}
}

const Context = React.createContext<Context>(noopContext)
const useContext = () => React.use(Context)

type Context = {
  isDesktop: boolean
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}
